import { useForm, usePage } from "@inertiajs/react"
import { AuthShell } from "@/components/layouts/auth-shell"
import { Button } from "@/components/ui/button"
import { t } from "@/lib/i18n"
import { Mail, Check, Plus, Store } from "lucide-react"

type InviteDetails = {
  id: number
  role: string
  roleLabel: string
  restaurantName: string
}

type SharedUser = {
  name: string
  email: string
  initials: string
}

interface StaffInviteProps {
  invite: InviteDetails
}

export default function StaffInviteShow({ invite }: StaffInviteProps) {
  const { user } = usePage<{ user: SharedUser }>().props
  const { post, processing } = useForm()

  const handleAccept = (e: React.FormEvent) => {
    e.preventDefault()
    post("/staff_invite/accept")
  }

  const handleDecline = (e: React.FormEvent) => {
    e.preventDefault()
    post("/staff_invite/decline")
  }

  return (
    <AuthShell title={t("ui.auth.titles.staff_invite")} headline={t("ui.auth.staff.headline")} blurb={t("ui.auth.staff.blurb")}>
      <div className="mb-8 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Mail className="h-6 w-6" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("ui.react.pages.staff.staff_invites.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("ui.react.pages.staff.staff_invites.description")}
        </p>
      </div>

      <div className="space-y-6">
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 text-center space-y-2">
          <p className="text-sm font-medium text-muted-foreground">
            {t("ui.react.pages.staff.staff_invites.hello", { name: user?.name })}
          </p>
          <h3 className="text-xl font-extrabold text-foreground tracking-tight flex items-center justify-center gap-2">
            <Store className="w-5 h-5 text-primary" />
            {invite.restaurantName}
          </h3>
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink bg-primary/10 px-3 py-1 rounded-full inline-block">
            {invite.roleLabel}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <form onSubmit={handleAccept} className="w-full">
            <Button
              type="submit"
              disabled={processing}
              className="w-full gap-2 font-bold bg-gradient-to-r from-primary to-primary/80 hover:opacity-90 shadow-md py-6 text-base"
            >
              <Check className="w-5 h-5" />
              {t("ui.react.pages.staff.staff_invites.accept_btn")}
            </Button>
          </form>

          <form onSubmit={handleDecline} className="w-full">
            <Button
              type="submit"
              variant="outline"
              disabled={processing}
              className="w-full gap-2 font-semibold py-6 border-border/60 hover:bg-muted/80 text-sm"
            >
              <Plus className="w-4 h-4 text-muted-foreground" />
              {t("ui.react.pages.staff.staff_invites.decline_btn")}
            </Button>
          </form>
        </div>
      </div>
    </AuthShell>
  )
}
